// Opt-in CRUD configuration for ModuleListView.  Only module keys present in
// MODULE_CRUD get the New / Edit / Delete / Audit controls; every other module
// keeps rendering as a pure read-only grid (zero behavioural change).
//
// Adding write support to a list screen = add one entry here + a matching
// backend controller (POST/PUT/DELETE) + reader/writer.  See the "set-methods"
// (Settings → Method) implementation for the reference pattern.

export type CrudFieldType = 'text' | 'textarea' | 'select' | 'multiselect' | 'checkbox' | 'date' | 'readonly' | 'file' | 'assocgrid';

export interface CrudField {
  /** Payload key sent to the API AND the key read back from GET {id}. */
  key: string;
  label: string;
  type: CrudFieldType;
  required?: boolean;
  maxLength?: number;
  /** OPTIONAL — initial value for a NEW record (legacy default-value parity, e.g.
   *  Default='Yes', Sort='0'). Ignored on Edit (the loaded record wins). Use a
   *  string for text/select, string[] for multiselect, boolean for checkbox. */
  defaultValue?: string | boolean | string[];
  /** OPTIONAL — show this field ONLY when editing an existing record; hide it on
   *  New. For a field the server defaults sensibly on create (e.g. Active, which
   *  the DB defaults to 1), so the New form stays as short as the record needs.
   *  A hidden field is not rendered, not required-checked, and NOT posted — so
   *  the server-side default applies. */
  editOnly?: boolean;
  /** For type 'select' — GET this URL for options ({ data: [{ id, name }] }). */
  optionsUrl?: string;
  /** For type 'select' — a STATIC option list, used instead of `optionsUrl` (no
   *  request is made). For choices the client already knows: a fixed vocabulary
   *  (Active / Disabled) or options derived from the record's own parent, which
   *  no endpoint could serve independently. `optionsUrl` still wins if both are
   *  set, so this is additive for every existing config. */
  options?: { id: string; name: string }[];
  /** For type 'select' — label of the empty/none option (default "— None —"). */
  noneLabel?: string;
  /** OPTIONAL — greyed placeholder text shown when the field is empty. For a
   *  'readonly' field it replaces the default "(auto)" filler (e.g. the legacy
   *  Sharing Code shows "Autogenerated"). */
  placeholder?: string;
  /** For 'select' / 'multiselect' — CASCADE: the key of the PARENT field whose
   *  value filters this field's options. When set, options are (re)loaded from
   *  `optionsUrl?{dependsOn}={parentValue}` whenever the parent changes, and this
   *  field is CLEARED when the parent changes. Empty parent ⇒ empty options.
   *  Additive: fields without `dependsOn` load their full option list once. */
  dependsOn?: string;
  /** For type 'multiselect' — paginate the checked-list at this many rows per
   *  page (numbered pager, legacy CheckedListBoxEditor parity). Omit for a plain
   *  scrolling list. */
  pageSize?: number;
  /** For type 'multiselect' — when set, the field box lists EVERY selected value
   *  joined by this separator (e.g. "; ") instead of "N selected". Legacy XAF
   *  editor parity. */
  summarySeparator?: string;
  /** For type 'multiselect' — hide the "OK" button in the picker popup (it still
   *  closes on click-away / Escape). Legacy checked-list parity. */
  hideOk?: boolean;
  /** Optional helper text under the field. */
  hint?: string;
  /** OPTIONAL — override the renderer for INLINE row edit only (dialog still
   *  uses `type`). LIMS QcRole is the canonical case: stored as a FK
   *  (Guid) and rendered as a dropdown in the dialog, but rendered as a
   *  plain text input INLINE (per the LIMS XAFML override). When set to
   *  'text' on a select field, the inline editor sends the typed string
   *  as `{key}Name` (the server resolves to Oid via sub-SELECT). */
  inlineType?: CrudFieldType;
  /** OPTIONAL — LIMS-verbatim message shown inline when the field is
   *  required and empty. Falls back to "{label} Should not be empty."
   *  if not supplied. Use the EXACT wording from the legacy entity's
   *  [RuleRequiredField] attribute so the client message matches what
   *  the server would emit (CLAUDE.md "LIMS exact parity"). */
  requiredMessage?: string;
  /** For type 'file' — the sibling form key holding the uploaded file's
   *  display name (returned by GET {basePath}/{id} alongside the FK Oid).
   *  The field's own `key` holds the FileData Oid sent back on Save. */
  fileNameKey?: string;
  /** For type 'file' — POST endpoint that accepts a multipart `file` and
   *  returns { data: { fileDataOid, fileName } } (two-phase upload). */
  uploadUrl?: string;
  /** For type 'file' — GET base; the field downloads `${downloadUrlBase}/${oid}/download`. */
  downloadUrlBase?: string;
}

export interface ModuleCrudConfig {
  /** REST base, e.g. '/api/v1/methods'.  POST base, PUT/DELETE base/{id}. */
  basePath: string;
  /** Row property holding the record's primary key (Oid) for edit/delete. */
  idField: string;
  /** Audit FormName + GET base/{id} prefill source. */
  formName: string;
  /** Singular noun for button/dialog text ("Method"). */
  entityLabel: string;
  fields: CrudField[];
  /** OPTIONAL — maps a list-view column key to the editable CRUD field key
   *  when they differ. Needed for FK columns where the list shows the
   *  human-readable name (e.g. `qcSource`) but the editor needs the FK
   *  Oid (e.g. `qcSourceId`). Identity mapping is used when omitted. */
  columnFieldMap?: Record<string, string>;
  /** OPTIONAL — set to false to hide the per-row inline-edit pencil. The
   *  full-dialog edit (external-link icon) still shows. Use for SIMPLE
   *  master-data lists (Container / Preservative / Temperature) where
   *  cells are too few to benefit from inline edit; only QC Type and
   *  other multi-column forms keep inline on. Default: true. */
  inlineRowEdit?: boolean;
  /** OPTIONAL — initial page-size for this module's list (one of PAGE_SIZES:
   *  10 / 20 / 50 / 100).  Overrides the global DEFAULT_PAGE_SIZE for THIS form
   *  only.  Omit to use the shared default. */
  defaultPageSize?: number;
  /** OPTIONAL — set true for lookups that support bulk CSV load (BO UI
   *  Standard §6).  Adds the Import + Template toolbar buttons: Template
   *  downloads a blank CSV of the non-readonly field labels; Import uploads a
   *  filled CSV to `${basePath}/import`.  Only Method supports this today. */
  bulkLoad?: boolean;
  /** OPTIONAL — the settings FAMILY this form belongs to (BO UI Standard §4
   *  <RelatedLookups>).  Renders a strip under the toolbar to hop between the
   *  sibling lookups; the entry whose `id` matches this module is the active
   *  (orange) one.  A sibling whose form isn't built yet shows a gentle notice
   *  on click instead of navigating. */
  relatedLookups?: { id: string; label: string }[];
  /** OPTIONAL — detail view: hide the Edit button (view + delete only, no edit
   *  of existing records from the detail). Default: Edit shown. */
  detailHideEdit?: boolean;
  /** OPTIONAL — detail view: show a Delete button on the toolbar (ALPACA
   *  detail-toolbar parity), for existing records. Default: off (delete stays a
   *  list-view action). Runs the shared useDeleteFlow → DELETE basePath/{id}. */
  detailShowDelete?: boolean;
  /** OPTIONAL — detail view: open an EXISTING record straight into editable mode
   *  (Save · Save & Close · Save & New · Cancel + Delete), skipping the read-only
   *  view + separate Edit toggle. Mirrors the legacy XAF flow where List → Edit
   *  opens an editable DetailView. Default: open read-only (Edit to modify). */
  detailOpenInEdit?: boolean;
  /** OPTIONAL — detail view: stack every field in ONE vertical column instead of
   *  the default two-column grid. Use when the legacy XAF DetailView is a single
   *  stacked column (e.g. Bottle Sharing) so the field order reads strictly
   *  top-to-bottom. Additive — default (unset) keeps the two-column layout. */
  singleColumn?: boolean;
  /** OPTIONAL — detail view: hide the "General Information" section caption above
   *  the field grid. Default (unset) shows it. */
  hideSectionHeader?: boolean;
  /** OPTIONAL — open New/Edit in the shared <ModuleCrudDialog> POPUP instead of
   *  the full-page <SettingsDetailPage>.
   *
   *  Standing rule 12 makes the full-page detail the default for every CRUD
   *  form, and it stays the default — this is an explicit per-form opt-out for
   *  a screen whose approved design is a compact modal (Settings ▸ Permit
   *  Library New ▸ Permit Type New, owner-specified 2026-08-27). Set it ONLY
   *  when the form owner has signed off on the popup; a two-or-three-field
   *  lookup is the only shape it suits. */
  dialogEdit?: boolean;
  /** OPTIONAL — detail view: the field key whose value captions the record in the
   *  header/breadcrumb ("{entityLabel} – {value}"), mirroring the legacy XAF
   *  <c>[DefaultProperty]</c>. For a 'select' field the option label is shown
   *  (not the raw id), and the caption appears even on a NEW record once the
   *  field has a value. Default (unset) uses the first field. */
  titleField?: string;
}

export const MODULE_CRUD: Record<string, ModuleCrudConfig> = {
  // Settings → Method.  Mirrors the ALPACA "Method" screen toolbar
  // (New / Delete) plus per-row Edit, with audit on update/delete.
  'set-methods': {
    basePath: '/api/v1/methods',
    idField: 'oid',
    formName: 'Method',
    entityLabel: 'Method',
    // Method supports bulk CSV load (Import + Template toolbar buttons).
    bulkLoad: true,
    // Settings family strip (BO UI Standard §4).  Method is the active entry;
    // the three siblings aren't built as forms yet (they notify on click).
    relatedLookups: [
      { id: 'set-method-category', label: 'Method Category' },
      { id: 'set-method-number',   label: 'Method Number' },
      { id: 'set-standard-clause', label: 'Standard Clause' },
      { id: 'set-methods',         label: 'Method' },
    ],
    // Field order mirrors the ALPACA "Method" detail form.
    fields: [
      { key: 'methodName',     label: 'Method',          type: 'text',     required: true },
      { key: 'methodNumber',   label: 'Method Number',   type: 'text',     required: true, maxLength: 200 },
      { key: 'categoryId',     label: 'Category',        type: 'select',   optionsUrl: '/api/v1/methods/categories', noneLabel: 'N/A' },
      { key: 'standardClause', label: 'Standard Clause', type: 'text' },
      { key: 'clauseSubject',  label: 'Clause Subject',  type: 'text' },
      { key: 'activeDate',     label: 'Active Date',     type: 'date' },
      { key: 'dateIssued',     label: 'Date Issued',     type: 'date' },
      { key: 'methodCode',     label: 'Method Code',     type: 'readonly', hint: 'Auto-generated on save' },
      { key: 'requirement',    label: 'Requirement',     type: 'textarea' },
      { key: 'comment',        label: 'Comment',         type: 'textarea' },
      { key: 'active',         label: 'Active',          type: 'checkbox' },
    ],
  },

  // Settings → Test Code.  Mirrors the ALPACA "Test Code" screen toolbar
  // (New / Edit / Delete + per-row pencil for inline-edit & open-detail) with
  // audit on update/delete (CLAUDE.md §14a Rule A).  Legacy entity is
  // Modules.BusinessObjects.Setting.TestMethod; this slice covers the General
  // tab fields. TestCode is server-generated (race-safe ISequentialIdGenerator,
  // CLAUDE.md §14a Rule B — replaces the legacy GetTestuqID sproc) and shown
  // read-only on edit. Tabs 2-17 (Surrogates, Internal Standards, Components,
  // Test Guides, Prep Methods, QC Types, Instruments, Department Chains,
  // Sample Prep Chains, Preservatives, Items) land in follow-up slices.
  'set-testcode': {
    basePath: '/api/v1/test-methods',
    idField: 'oid',
    formName: 'Test Code',
    entityLabel: 'Test Code',
    // Test Code uses the bespoke TestCodeFormDialog (tabs + pickers,
    // not the generic ModuleCrudDialog). Inline cell editing doesn't
    // make sense here — the row has too many fields and several use
    // pickers/lookups (Method/QC Types/Test Guides) that can't be
    // edited in a single grid row. Dialog edit only.
    inlineRowEdit: false,
    // Field order mirrors the LIMS TestMethod_DetailView General tab.
    fields: [
      { key: 'testName',     label: 'Test',          type: 'text',     required: true },
      { key: 'matrixId',     label: 'Matrix',        type: 'select',   required: true, optionsUrl: '/api/v1/test-methods/matrices',    noneLabel: '— Select —' },
      { key: 'methodId',     label: 'Method',        type: 'select',   optionsUrl: '/api/v1/test-methods/methods',     noneLabel: '— Select —',
        hint: 'Required unless this is a group test.' },
      { key: 'category',     label: 'Category',      type: 'text' },
      { key: 'departmentId', label: 'Department',    type: 'select',   optionsUrl: '/api/v1/test-methods/departments', noneLabel: '— None —' },
      { key: 'isGroup',      label: 'Is Group',      type: 'checkbox' },
      { key: 'isFieldTest',  label: 'Is Field Test', type: 'checkbox' },
      { key: 'retireDate',   label: 'Retire Date',   type: 'date' },
      { key: 'comment',      label: 'Comment',       type: 'textarea' },
      { key: 'testCode',     label: 'Test Code',     type: 'readonly', hint: 'Auto-generated on save' },
    ],
  },

  // Settings → SDMS → Formula Library.  Mirrors the ALPACA "SDMS Formulas"
  // detail form fields verbatim (screenshot 2026-07-28). The SHARED formula
  // DEFINITIONS used by both SDMS templates and Result Entry Formula — New /
  // Edit / Delete + audit; Delete is blocked server-side while a Result Entry
  // Formula mapping still references the row.
  'formulalib': {
    basePath: '/api/v1/formula-library',
    idField: 'uqId',
    formName: 'Formula Library',
    entityLabel: 'Formula',
    fields: [
      { key: 'formulaID',   label: 'Formula ID',   type: 'readonly', hint: 'Auto-generated on save' },
      { key: 'formulaName', label: 'Formula Name', type: 'text',     required: true, maxLength: 100 },
      { key: 'kind',        label: 'Kind',          type: 'select',   required: true, optionsUrl: '/api/v1/formula-library/kinds' },
      { key: 'apiFunction', label: 'Api Function',  type: 'text',     hint: 'Required when Kind = Fixed (e.g. CalculateRPD).' },
      { key: 'targetField', label: 'Target Field',  type: 'text',     required: true, hint: 'The row field this formula writes (e.g. RPD, %Recovery, Result).' },
      { key: 'series',      label: 'Series',        type: 'text',     required: true, hint: 'Numeric — also the default execution order.' },
      { key: 'caption',     label: 'Caption',       type: 'text' },
      { key: 'isActive',    label: 'Is Active',     type: 'checkbox' },
      { key: 'script',      label: 'Script',        type: 'textarea', hint: 'Required when Kind = Script (C# body).' },
      { key: 'description', label: 'Description',   type: 'textarea' },
    ],
  },

  // Settings → Holidays.  Mirrors the ALPACA "Holidays" screen (HolidayName +
  // HolidayDate) with New / Edit / Delete + audit.
  'set-holidays': {
    basePath: '/api/v1/holidays',
    idField: 'oid',
    formName: 'Holidays',
    entityLabel: 'Holiday',
    fields: [
      { key: 'holidayName', label: 'Holiday Name', type: 'text', required: true },
      { key: 'holidayDate', label: 'Holiday Date', type: 'date', required: true },
    ],
  },

  // Settings → Utility → Test → Container.  Mirrors the ALPACA
  // "Container" screen (entity Modules.BusinessObjects.Setting.Container).
  // Fields per the LIMS Container_DetailView: ContainerName (required,
  // unique), ContainerCode (optional), Comment (optional). The XAF
  // [RuleUniqueValue] on ContainerName is enforced server-side via the
  // ExistsByName check; client surfaces the 400 message as a toast.
  'set-container': {
    basePath: '/api/v1/containers',
    idField: 'oid',
    formName: 'Container',
    entityLabel: 'Container',
    // Simple 3-field master — no inline row edit, dialog only.
    inlineRowEdit: false,
    fields: [
      { key: 'containerName', label: 'Container',      type: 'text', required: true },
      { key: 'containerCode', label: 'Container Code', type: 'text' },
      // Comment is a multi-line textarea in the LIMS Container_DetailView
      // (XAF default for nvarchar(MAX)). 3 rows ~= LIMS textbox height.
      { key: 'comment',       label: 'Comment',        type: 'textarea' },
    ],
  },

  // Settings → Data Package Review Checklist.  Mirrors the ALPACA
  // "DataPackageCheckList" screen (entity
  // Modules.BusinessObjects.DataPackage.DataPackageCheckList).
  // Fields per the legacy DataPackageCheckList_DetailView: CheckPoint (required,
  // unique), Department (text), Tests (multi-select TestMethod), QcRoles,
  // QcRootRoles, Default (Yes/No/NA), Sort, Comment.
  'data-package-checklist': {
    basePath: '/api/v1/data-package-checklists',
    idField: 'oid',
    formName: 'Data Package Review Checklist',
    entityLabel: 'Check Point',
    inlineRowEdit: false,
    defaultPageSize: 20,
    detailShowDelete: true,
    // Single-column detail layout (label above field, stacked vertically) to
    // match the legacy DataPackageCheckList_DetailView arrangement.
    singleColumn: true,
    // Hide the "General Information" section caption (user request 2026-08-26).
    hideSectionHeader: true,
    fields: [
      // Auto-generated on save (3-digit zero-padded) — display only; shows the
      // greyed "Autogenerate" placeholder like the legacy DetailView.
      { key: 'checkPointId', label: 'Check Point ID', type: 'readonly', placeholder: 'Autogenerate' },
      { key: 'checkPoint',   label: 'Check Point',   type: 'text',        required: true,
        requiredMessage: "'Check Point' must not be empty." },
      // Department is a multi-select of dbo.Department (legacy CheckedListBox),
      // stored as the "; "-joined Department-Oid string (grid resolves to names).
      { key: 'departments',  label: 'Department',    type: 'multiselect',
        optionsUrl: '/api/v1/data-package-checklists/departments',
        pageSize: 10, summarySeparator: '; ', hideOk: true },
      // Test Methods — legacy Link/Unlink association grid (TestName · MethodName
      // · FormulaCount · IsFormulaActive). Stores the same "; "-joined
      // TestMethod-Oid list; rendered as a full-width grid section below the form.
      { key: 'tests',        label: 'Test Methods',  type: 'assocgrid',
        optionsUrl: '/api/v1/data-package-checklists/test-method-rows' },
      { key: 'qcRoles',      label: 'QC Roles',      type: 'select',
        optionsUrl: '/api/v1/data-package-checklists/qc-roles', noneLabel: 'N/A' },
      { key: 'qcRootRoles',  label: 'QC Root Roles', type: 'select',
        optionsUrl: '/api/v1/data-package-checklists/qc-root-roles', noneLabel: 'N/A' },
      // Legacy default: Sort = 0.
      { key: 'sort',         label: 'Sort',          type: 'text', defaultValue: '0' },
      // Legacy DefaultOption enum default = Yes.
      { key: 'defaultOption', label: 'Default', type: 'select',
        options: [{ id: 'Yes', name: 'Yes' }, { id: 'No', name: 'No' }, { id: 'NA', name: 'N/A' }],
        defaultValue: 'Yes', noneLabel: '— None —' },
      { key: 'comment',      label: 'Comment',       type: 'textarea' },
    ],
  },

  // Settings → Analysis → Contract Lab.  Mirrors the ALPACA "Contract Lab"
  // screen (entity Modules.BusinessObjects.SuboutTracking.SubOutContractLab).
  // Fields per the legacy SubOutContractLab_DetailView: ContractLabName
  // (required, unique) + EmailID (required); the rest are optional. The
  // Certificate is a FileData attachment (upload/download via the shared
  // 'file' field type + the certificate upload/download endpoints).
  'set-contractlab': {
    basePath: '/api/v1/contract-labs',
    idField: 'oid',
    formName: 'Contract Lab',
    entityLabel: 'Contract Lab',
    inlineRowEdit: false,
    // Detail flow (legacy parity): a ROW click opens read-only (view + Edit
    // button); the Edit pencil / toolbar Edit opens editable.
    detailShowDelete: true,
    fields: [
      { key: 'contractLabName', label: 'Contract Lab',    type: 'text',     required: true,
        requiredMessage: "'Contract Lab' must not be empty." },
      { key: 'accreditationID', label: 'Accreditation ID', type: 'text' },
      { key: 'contact',         label: 'Contact',          type: 'text' },
      { key: 'phone',           label: 'Phone',            type: 'text' },
      { key: 'emailID',         label: 'Email',            type: 'text',     required: true,
        requiredMessage: "'Email' must not be empty." },
      { key: 'address1',        label: 'Address',          type: 'text' },
      { key: 'address2',        label: 'Address 2',        type: 'text' },
      { key: 'city',            label: 'City',             type: 'text' },
      { key: 'state',           label: 'State',            type: 'text' },
      { key: 'zip',             label: 'Zip',              type: 'text' },
      { key: 'country',         label: 'Country',          type: 'text' },
      { key: 'webSite',         label: 'Website',          type: 'text' },
      // Certificate — file attachment stored in dbo.FileData (FK on
      // SubOutContractLab.Certificate). Two-phase upload: pick a file → POST
      // upload → Oid stored in `certificate` on Save; download via the Oid.
      { key: 'certificate',     label: 'Certificate',      type: 'file',
        fileNameKey: 'certificateFileName',
        uploadUrl: '/api/v1/contract-labs/certificate/upload',
        downloadUrlBase: '/api/v1/contract-labs/certificate' },
      { key: 'comment',         label: 'Comment',          type: 'textarea' },
      { key: 'deactivate',      label: 'Deactivate',       type: 'checkbox' },
    ],
  },

  // Settings → Analysis → Bottle Sharing.  Mirrors the ALPACA "Bottle Sharing"
  // screen (entity Modules.BusinessObjects.Setting.BottleSharing).  Fields per
  // the legacy BottleSharing_DetailView: SharingCode (auto-generated, read-only),
  // SampleMatrix (VisualMatrix FK), Matrix (FK), Test (multi-select TestMethod,
  // required).  The legacy form cascades SampleMatrix → Matrix → Test; here the
  // three pickers are independent selects (the shared detail renderer has no
  // cascade support) — same columns + same stored data (Tests persists as the
  // "; "-joined TestMethod-Oid string).
  'set-bottlesharing': {
    basePath: '/api/v1/bottle-sharings',
    idField: 'oid',
    formName: 'Bottle Sharing',
    entityLabel: 'Bottle Sharing',
    inlineRowEdit: false,
    // Detail flow (legacy parity): a ROW click opens read-only (view + Edit
    // button); the Edit pencil / toolbar Edit opens editable. Delete shows in
    // both view and edit toolbars.
    detailShowDelete: true,
    // Single stacked column to match the legacy BottleSharing_DetailView
    // (SharingCode → SampleMatrix → Matrix → Test, one field per row).
    singleColumn: true,
    // Header caption = Sample Matrix (legacy [DefaultProperty("SampleMatrix")] —
    // "Bottle Sharing - Drinking Water").
    titleField: 'sampleMatrix',
    fields: [
      // Auto-generated on save (3-digit zero-padded) — display only; shows the
      // greyed "Autogenerated" placeholder like the legacy form.
      { key: 'sharingCode',  label: 'Sharing Code',  type: 'readonly', placeholder: 'Autogenerated' },
      { key: 'sampleMatrix', label: 'Sample Matrix', type: 'select',
        optionsUrl: '/api/v1/bottle-sharings/sample-matrices', noneLabel: 'N/A' },
      // Cascade (legacy BottleSharing): Matrix is filtered by the chosen Sample
      // Matrix; Test is filtered by the chosen Matrix. Changing a parent clears
      // its children and reloads their options.
      { key: 'matrix',       label: 'Matrix',        type: 'select', dependsOn: 'sampleMatrix',
        optionsUrl: '/api/v1/bottle-sharings/matrices', noneLabel: 'N/A' },
      { key: 'tests',        label: 'Test',          type: 'multiselect', required: true,
        dependsOn: 'matrix', pageSize: 10, summarySeparator: '; ', hideOk: true,
        requiredMessage: 'Tests must not be empty.',
        optionsUrl: '/api/v1/bottle-sharings/tests' },
    ],
  },

  // Settings → Utility → Test → Preservative.  Mirrors the ALPACA
  // "Preservative" screen (entity Modules.BusinessObjects.Setting.Preservative).
  // Fields per LIMS Preservative_DetailView: PreservativeName (required,
  // unique — XAF [RuleRequiredField] + [RuleUniqueValue]), PreservativeCode
  // (optional), Comment (optional, max 1000). Comment is hidden in LIMS
  // list view but visible in detail.
  'set-preservative': {
    basePath: '/api/v1/preservatives',
    idField: 'oid',
    formName: 'Preservative',
    entityLabel: 'Preservative',
    // Simple 3-field master — no inline row edit, dialog only.
    inlineRowEdit: false,
    fields: [
      { key: 'preservativeName', label: 'Preservative',      type: 'text', required: true },
      { key: 'preservativeCode', label: 'Preservative Code', type: 'text' },
      // Comment is multi-line in LIMS Preservative_DetailView ([Size(1000)]
      // on the entity, line 76). 3 rows ~= LIMS textbox height.
      { key: 'comment',          label: 'Comment',           type: 'textarea' },
    ],
  },

  // Settings → Utility → Test → Temperature.  Mirrors the ALPACA
  // "Temperature" screen (entity Modules.BusinessObjects.Setting.Temperature).
  // Single field — TemperatureName (required, unique). The XAF entity has
  // no Comment, no Code, no audit columns — minimal master.
  'set-temperature': {
    basePath: '/api/v1/temperatures',
    idField: 'oid',
    formName: 'Temperature',
    entityLabel: 'Temperature',
    // Single-field master — no inline row edit, dialog only.
    inlineRowEdit: false,
    fields: [
      { key: 'temperatureName', label: 'Temperature Name', type: 'text', required: true },
    ],
  },

  // Settings → Utility → Test → QC Type.  Mirrors the ALPACA "QCType"
  // screen (entity Modules.BusinessObjects.Setting.QCType).  Six fields:
  // QCTypeName (required, unique), Comment (textarea, max 1000), three
  // FK lookups (QCSource, QcRole, QCRootRole), and the ExcludedReport
  // bit. Lookup options come from /api/v1/qc-types/lookups/{kind}.
  'set-qctype': {
    basePath: '/api/v1/qc-types',
    idField: 'oid',
    formName: 'QC Type',
    entityLabel: 'QC Type',
    // List columns return joined human-readable names; the editable
    // crud fields use the FK Oid. Map list-column → editable-field so
    // the row-edit machinery knows which control to render per cell.
    columnFieldMap: {
      qcType:         'qcTypeName',
      qcSource:       'qcSourceId',
      qcRole:         'qcRoleId',
      qcRootRole:     'qcRootRoleId',
      excludedReport: 'excludedReport',
    },
    // Field order verified against the LIMS QCType_DetailView screenshot
    // (Air Density Blank record): QC Type → QC Role → QC Source → QC Root
    // Role → Comment → Excluded Report. All three FK lookups are dropdowns
    // with create + erase chrome — verified against the LIMS New-form
    // screenshot.
    fields: [
      { key: 'qcTypeName',     label: 'QC Type',        type: 'text', required: true,
        // LIMS QCType.cs:68 — [RuleRequiredField("QCTypeName", … , "QC Type must not be empty")]
        requiredMessage: 'QC Type must not be empty.' },
      // LIMS uses "N/A" (not "— Select —") as the empty-state label on
      // these three dropdowns — matches the legacy XAF placeholder.
      { key: 'qcRoleId',       label: 'QC Role',        type: 'select',
        optionsUrl: '/api/v1/qc-types/lookups/qc-role',      noneLabel: 'N/A',
        // LIMS XAFML overrides QcRole to a STRING editor in inline edit
        // (the QCType_ListView grid screenshot confirms QcRole is a text
        // cell, no dropdown chrome). Dialog stays a dropdown.
        inlineType: 'text' },
      { key: 'qcSourceId',     label: 'QC Source',      type: 'select',
        optionsUrl: '/api/v1/qc-types/lookups/qc-source',    noneLabel: 'N/A' },
      { key: 'qcRootRoleId',   label: 'QC Root Role',   type: 'select',
        optionsUrl: '/api/v1/qc-types/lookups/qc-root-role', noneLabel: 'N/A' },
      { key: 'comment',        label: 'Comment',        type: 'textarea' },
      { key: 'excludedReport', label: 'Excluded Report', type: 'checkbox' },
    ],
  },

  // Settings → Utility → Test → QC Role. Mirrors the ALPACA "QcRole"
  // screen (entity Modules.BusinessObjects.Setting.QcRole). The XAFML
  // override hides the four classifier bits from the detail view —
  // LIMS only surfaces QC Role name (verified screenshot 2026-06-30).
  // Backend still accepts the bits (they default to false on Create
  // and stay unchanged on Update when omitted from the payload).
  // Simple master — no inline row edit, dialog only.
  'set-qcrole': {
    basePath: '/api/v1/qc-roles',
    idField: 'oid',
    formName: 'QC Role',
    entityLabel: 'QC Role',
    inlineRowEdit: false,
    fields: [
      { key: 'qcRoleName', label: 'QC Role', type: 'text', required: true,
        // LIMS QcRole.cs:25 — [RuleRequiredField("Enter the QC Role", …)]
        requiredMessage: 'Enter the QC Role' },
    ],
  },

  // ── Settings → Reporting → MSR → Water System (merged team package
  // 2026-07-02).  Legacy dbo.MSRWaterSystem — SystemID is server-minted
  // (race-safe, §14a Rule B) and read-only; SystemName is unique (server-
  // enforced inside the mint transaction). ────────────────────────────────
  'msr-water-system': {
    basePath: '/api/v1/msr-water-system',
    idField: 'oid',
    formName: 'MSR Water System',
    entityLabel: 'Water System',
    inlineRowEdit: false,
    fields: [
      { key: 'systemId',   label: 'System ID',   type: 'readonly',
        hint: 'Assigned automatically on save.' },
      { key: 'systemName', label: 'System Name', type: 'text', required: true, maxLength: 200,
        requiredMessage: 'System Name is required.' },
      { key: 'description', label: 'Description', type: 'textarea' },
    ],
  },

  // ── Settings → Reporting → MSR → MSR Data Import Config (follow-up slice to
  // the 2026-07-02 MSR merge).  The New/Edit detail is the BESPOKE
  // <MsrDataImportConfigDialog> — five type-to-search multi-select GUID pickers
  // + an ImportMode radio that the generic CrudField types can't express — so
  // ModuleListView special-cases the 'msr-data-import-config' key (same as 'msr')
  // and `fields` stays empty (the generic field editor / inline edit never
  // render).  This entry still drives the toolbar New/Edit/Delete wiring, the
  // audit FormName, and bulk delete (DELETE {basePath}/{oid} soft-deletes the
  // config; the writer sets GCRecord). ─────────────────────────────────────────
  'msr-data-import-config': {
    basePath: '/api/v1/msr-data-import-config',
    idField: 'oid',
    formName: 'MSR Data Import Config',
    entityLabel: 'Data Import Config',
    inlineRowEdit: false,
    fields: [],
  },

  // ── Settings → Dashboard → Assign Dashboard To User (ported from the ALPACA
  // AssignDashboardToUserDepartment_ListView).  New/Edit opens the SHARED
  // full-page <SettingsDetailPage> (Standing rule 12 / BO UI Standard §4 — NOT a
  // popup), exactly like ALPACA's detail view: Dashboard View + Navigation Item
  // dropdowns, Role as a checked-list DROPDOWN ('multiselect', rendered via the
  // shared CheckedCombo — same interaction as the legacy XAF CheckedListBoxEditor),
  // and Is Active.  The Role field stores an array of RoleNavigationPermission
  // Oids (the backend joins them to "; " for the shared table); GET {id} returns
  // roleIds[] and POST/PUT accept roleIds[].  Audit + delete unchanged. ────────
  'set-assign-dashboard': {
    basePath: '/api/v1/assign-dashboard',
    idField: 'oid',
    formName: 'Assign Dashboard To User',
    entityLabel: 'Dashboard Assignment',
    inlineRowEdit: false,
    fields: [
      { key: 'dashboardViewId', label: 'Dashboard View', type: 'select', required: true,
        optionsUrl: '/api/v1/assign-dashboard/dashboards', noneLabel: 'Select a Dashboard View…',
        requiredMessage: 'Dashboard View is required.' },
      { key: 'navigationItemId', label: 'Navigation Item', type: 'select',
        optionsUrl: '/api/v1/assign-dashboard/navigation-items', noneLabel: '— None —' },
      { key: 'roleIds', label: 'Role', type: 'multiselect',
        optionsUrl: '/api/v1/assign-dashboard/roles', noneLabel: 'Select roles…' },
      { key: 'isActive', label: 'Is Active', type: 'checkbox' },
    ],
  },

  // ── Reporting → Monthly Summary Repository (merged team package
  // 2026-07-02).  The New/Edit detail is the BESPOKE <MsrDetailDialog>
  // (Year/Month/Category + Retrieve + samples grid) — ModuleListView
  // special-cases the 'msr' key, so `fields` stays empty (the generic
  // field editor / inline edit never render).  This entry still drives the
  // toolbar New/Edit/Delete wiring, the audit FormName, and bulk delete
  // (DELETE {basePath}/{oid} soft-deletes the header + child samples). ───
  'msr': {
    basePath: '/api/v1/monthly-summary-repository',
    idField: 'oid',
    formName: 'Monthly Summary Repository',
    entityLabel: 'Monthly Summary',
    inlineRowEdit: false,
    fields: [],
  },

  // Sample Field Configuration (legacy VisualMatrix field config). ModuleListView
  // special-cases this key (INLINE_DETAIL_MODULES) — New/Edit render the bespoke
  // full-page SampleFieldConfigDetail (header + 3 tabs of available↔selected
  // fields), so `fields` stays empty. This entry drives the toolbar New/Edit/
  // Delete wiring + the audit FormName. (2026-08-25)
  'set-samplefieldconfig': {
    basePath: '/api/v1/sample-field-config',
    idField: 'oid',
    formName: 'Sample Field Configuration',
    entityLabel: 'Sample Field Configuration',
    inlineRowEdit: false,
    fields: [],
  },

  // ══════════════════════════════════════════════════════════════════════════
  //  Settings master-data lookups (merged team package 2026-07-02).
  //  Simple code-list CRUD over existing legacy dbo.* tables — each renders
  //  through the shared ModuleListView + SettingsDetailPage (New/Edit/Delete +
  //  audit on Update/Delete).  Grid list is served by the matching
  //  ModuleRegistry ModuleDef.  QC Role (set-qcrole) + Temperature
  //  (set-temperature) already exist above / from the MSR merge, so the
  //  package's duplicate verticals were skipped.
  // ══════════════════════════════════════════════════════════════════════════

  // Settings ▸ Utility ▸ Test ▸ Sample Category.
  'set-sample-category': {
    basePath: '/api/v1/sample-categories',
    idField: 'oid',
    formName: 'Sample Category',
    entityLabel: 'Sample Category',
    inlineRowEdit: false,
    fields: [
      { key: 'sampleCategoryName', label: 'Sample Category', type: 'text', required: true, maxLength: 100 },
      { key: 'comment', label: 'Comment', type: 'textarea', maxLength: 1000 },
    ],
  },

  // Settings ▸ Utility ▸ Test ▸ Units.
  'set-units': {
    basePath: '/api/v1/units',
    idField: 'oid',
    formName: 'Units',
    entityLabel: 'Unit',
    inlineRowEdit: false,
    fields: [
      { key: 'unitName', label: 'Unit', type: 'text', required: true, maxLength: 100 },
      { key: 'comment', label: 'Comment', type: 'textarea', maxLength: 1000 },
    ],
  },

  // Settings ▸ Utility ▸ Test ▸ Storage.
  'set-storage': {
    basePath: '/api/v1/storages',
    idField: 'oid',
    formName: 'Storage',
    entityLabel: 'Storage',
    inlineRowEdit: false,
    fields: [
      { key: 'storageName', label: 'Name', type: 'text', required: true, maxLength: 200 },
      { key: 'storageId', label: 'Storage ID', type: 'text', maxLength: 200 },
      { key: 'comment', label: 'Comment', type: 'textarea', maxLength: 1000 },
    ],
  },

  // Settings ▸ Utility ▸ Test ▸ Method Category.
  'set-method-category': {
    basePath: '/api/v1/method-categories',
    idField: 'oid',
    formName: 'Method Category',
    entityLabel: 'Method Category',
    inlineRowEdit: false,
    fields: [
      { key: 'category', label: 'Method Category', type: 'text', required: true, maxLength: 100 },
      { key: 'description', label: 'Description', type: 'textarea', maxLength: 1000 },
    ],
  },

  // Settings ▸ Utility ▸ Test ▸ QC Source (ALPACA: not required, not unique).
  'set-qc-source': {
    basePath: '/api/v1/qc-sources',
    idField: 'oid',
    formName: 'QC Source',
    entityLabel: 'QC Source',
    inlineRowEdit: false,
    fields: [
      { key: 'qcSource', label: 'QC Source', type: 'text', maxLength: 200 },
    ],
  },

  // Settings ▸ Utility ▸ Test ▸ QC Root Role.
  'set-qc-root-role': {
    basePath: '/api/v1/qc-root-roles',
    idField: 'oid',
    formName: 'QC Root Role',
    entityLabel: 'QC Root Role',
    inlineRowEdit: false,
    fields: [
      { key: 'qcRootRole', label: 'QC Root Role', type: 'text', required: true, maxLength: 100 },
    ],
  },

  // Settings ▸ Utility ▸ Custom Report Builder ▸ Report Category.
  'set-report-category': {
    basePath: '/api/v1/report-categories',
    idField: 'oid',
    formName: 'Report Category',
    entityLabel: 'Report Category',
    inlineRowEdit: false,
    fields: [
      { key: 'category', label: 'Report Category', type: 'text', required: true, maxLength: 200 },
      { key: 'description', label: 'Description', type: 'textarea', maxLength: 1000 },
    ],
  },

  // Settings ▸ Utility ▸ Custom Report Builder ▸ Report Type.
  // DTO key `reportType` maps to the real dbo.ReportType.ReportTypes column.
  'set-report-type': {
    basePath: '/api/v1/report-types',
    idField: 'oid',
    formName: 'Report Type',
    entityLabel: 'Report Type',
    inlineRowEdit: false,
    fields: [
      { key: 'reportType', label: 'Report Type', type: 'text', required: true, maxLength: 200 },
      { key: 'description', label: 'Description', type: 'textarea', maxLength: 1000 },
    ],
  },

  // Settings ▸ Utility ▸ CRM ▸ Category (legacy CRMNotesCategory).
  'set-crm-category': {
    basePath: '/api/v1/crm-categories',
    idField: 'oid',
    formName: 'Category',
    entityLabel: 'Category',
    inlineRowEdit: false,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, maxLength: 100 },
    ],
  },

  // Settings ▸ Utility ▸ Client Request ▸ Request Category (name + Is Test Needed).
  'set-request-category': {
    basePath: '/api/v1/request-categories',
    idField: 'oid',
    formName: 'Request Category',
    entityLabel: 'Request Category',
    inlineRowEdit: false,
    fields: [
      { key: 'requestCategory', label: 'Request Category', type: 'text', required: true, maxLength: 100 },
      { key: 'isTestNeeded', label: 'Is Test Needed', type: 'checkbox' },
    ],
  },

  // Settings ▸ Utility ▸ CMDP ▸ CMDP Sample Category.
  'set-cmdp-sample-category': {
    basePath: '/api/v1/cmdp-sample-categories',
    idField: 'oid',
    formName: 'CMDP Sample Category',
    entityLabel: 'CMDP Sample Category',
    inlineRowEdit: false,
    fields: [
      { key: 'sampleCategory', label: 'Sample Category', type: 'text', required: true, maxLength: 100 },
      { key: 'description', label: 'Description', type: 'textarea', maxLength: 500 },
    ],
  },

  // Settings ▸ Utility ▸ Analysis ▸ Asbestos ▸ Asbestos Fibers.
  'set-asbestos-fibers': {
    basePath: '/api/v1/asbestos-fibers',
    idField: 'oid',
    formName: 'Asbestos Fibers',
    entityLabel: 'Asbestos Fiber',
    inlineRowEdit: false,
    fields: [
      { key: 'asbestosFibersName', label: 'Asbestos Fibers Name', type: 'text', required: true, maxLength: 100 },
    ],
  },

  // Settings ▸ Utility ▸ Analysis ▸ Asbestos ▸ Fiber Type.
  'set-fiber-types': {
    basePath: '/api/v1/fiber-types',
    idField: 'oid',
    formName: 'Fiber Type',
    entityLabel: 'Fiber Type',
    inlineRowEdit: false,
    fields: [
      { key: 'fiberType', label: 'Fiber Type', type: 'text', required: true, maxLength: 100 },
    ],
  },

  // Settings ▸ Utility ▸ Analysis ▸ Asbestos ▸ Fiber Type Values (14 optical props).
  'set-fiber-type-values': {
    basePath: '/api/v1/fiber-type-values',
    idField: 'oid',
    formName: 'Fiber Type Values',
    entityLabel: 'Fiber Type Values',
    inlineRowEdit: false,
    fields: [
      { key: 'fiberType', label: 'Fiber Type', type: 'text', required: true },
      { key: 'riAlpha', label: 'RIAlpha', type: 'text' },
      { key: 'gammaPrep', label: 'Gamma Prep', type: 'text' },
      { key: 'riGamma', label: 'RIGamma', type: 'text' },
      { key: 'gammaPara', label: 'Gamma Para', type: 'text' },
      { key: 'pleochroism', label: 'Pleochroism', type: 'text' },
      { key: 'birefringence', label: 'Birefringence', type: 'text' },
      { key: 'extinction', label: 'Extinction', type: 'text' },
      { key: 'elognation', label: 'Elognation', type: 'text' },
      { key: 'parallel', label: 'Parallel', type: 'text' },
      { key: 'perpenducular', label: 'Perpenducular', type: 'text' },
      { key: 'morphology', label: 'Morphology', type: 'text' },
      { key: 'isotropic', label: 'Isotropic', type: 'text' },
      { key: 'ri', label: 'RI', type: 'text' },
    ],
  },

  // Settings ▸ Utility ▸ Analysis ▸ Asbestos ▸ Materials.
  'set-materials': {
    basePath: '/api/v1/materials',
    idField: 'oid',
    formName: 'Materials',
    entityLabel: 'Material',
    inlineRowEdit: false,
    fields: [
      { key: 'materialName', label: 'Material Name', type: 'text', required: true },
    ],
  },

  // Settings ▸ Utility ▸ Analysis ▸ Asbestos ▸ Non-Asbestos Fibers.
  'set-non-asbestos-fibers': {
    basePath: '/api/v1/non-asbestos-fibers',
    idField: 'oid',
    formName: 'Non-Asbestos Fibers',
    entityLabel: 'Non-Asbestos Fiber',
    inlineRowEdit: false,
    fields: [
      { key: 'nonAsbestosFiberName', label: 'Non-Asbestos Fiber Name', type: 'text', required: true },
    ],
  },

  // Settings ▸ Utility ▸ Analysis ▸ Asbestos ▸ Non-Fibrous Components.
  // Field key keeps the legacy column spelling (nonFibrousComponenetName).
  'set-non-fibrous-components': {
    basePath: '/api/v1/non-fibrous-components',
    idField: 'oid',
    formName: 'Non-Fibrous Components',
    entityLabel: 'Non-Fibrous Component',
    inlineRowEdit: false,
    fields: [
      { key: 'nonFibrousComponenetName', label: 'Non-Fibrous Component Name', type: 'text', required: true },
    ],
  },

  // Settings ▸ Utility ▸ Analysis ▸ Asbestos ▸ Other Components.
  'set-other-components': {
    basePath: '/api/v1/other-components',
    idField: 'oid',
    formName: 'Other Components',
    entityLabel: 'Other Component',
    inlineRowEdit: false,
    fields: [
      { key: 'otherComponentName', label: 'Other Component Name', type: 'text', required: true },
    ],
  },

  // Settings ▸ Utility ▸ Analysis ▸ Asbestos ▸ Result Range.
  'set-result-range': {
    basePath: '/api/v1/result-range',
    idField: 'oid',
    formName: 'Result Range',
    entityLabel: 'Result Range',
    inlineRowEdit: false,
    fields: [
      { key: 'resultOne', label: 'Result One', type: 'text', required: true },
      { key: 'resultTwo', label: 'Result Two', type: 'text', required: true },
      { key: 'deleteDate', label: 'Delete Date', type: 'date' },
    ],
  },

  // Settings ▸ Utility ▸ Analysis ▸ Asbestos ▸ Unconventional Analysis
  // (legacy DataEntry — FK caption dropdown + Comment + Test).
  'set-unconventional-analysis': {
    basePath: '/api/v1/unconventional-analysis',
    idField: 'oid',
    formName: 'Unconventional Analysis',
    entityLabel: 'Unconventional Analysis',
    inlineRowEdit: false,
    fields: [
      { key: 'sourceCaption', label: 'BO Source Code Caption', type: 'select', optionsUrl: '/api/v1/unconventional-analysis/captions', noneLabel: '— None —' },
      { key: 'comment', label: 'Comment', type: 'textarea' },
      { key: 'test', label: 'Test', type: 'text' },
    ],
  },

  // ── Settings ▸ Permit Library New ▸ Limit Type ──
  // Backed by dbo.AI_LimitType through /api/v1/limit-types. Same popup shape as
  // its sibling Permit Type New: two fields on New, Active added on Edit.
  'pln-limit-type': {
    basePath: '/api/v1/limit-types',
    idField: 'oid',
    formName: 'Limit Type',
    entityLabel: 'Limit Type',
    inlineRowEdit: false,
    singleColumn: true,
    titleField: 'limitTypeName',
    dialogEdit: true,
    detailShowDelete: true,
    fields: [
      { key: 'limitTypeName', label: 'Limit Type', type: 'text', required: true, maxLength: 100,
        requiredMessage: "'Limit Type' must not be empty." },
      { key: 'description',   label: 'Description', type: 'text' },
      // editOnly - a new row is Active by the column default (DF_AI_LimitType_Active),
      // so the New dialog stays two fields, exactly like Permit Type New.
      { key: 'active',        label: 'Active', type: 'checkbox', defaultValue: true, editOnly: true },
    ],
  },

  // ── External Data Import — Permit Libraries + Sheet Mapping settings CRUD ──
  'edi-permit-types': {
    basePath: '/api/v1/permit-types',
    idField: 'oid',
    formName: 'Permit Types',
    entityLabel: 'Permit Type',
    fields: [
      { key: 'permitType', label: 'Permit Type', type: 'text',     required: true, maxLength: 100 },
      { key: 'definition', label: 'Definition',  type: 'textarea' },
      { key: 'mcl',        label: 'MCL',         type: 'checkbox', hint: 'Maximum Contaminant Level' },
      { key: 'scl',        label: 'SCL',         type: 'checkbox', hint: 'Secondary Contaminant Level' },
      { key: 'alert',      label: 'Alert',       type: 'checkbox', hint: 'Custom alert threshold' },
      { key: 'mrl',        label: 'MRL',         type: 'checkbox', hint: 'Maximum Residue Level' },
    ],
  },

  // ── Settings ▸ Permit Library New ▸ Permit Type (Lifecycle v5 rebuild) ──
  // Reuses the EXISTING /api/v1/permit-types controller + dbo.PermitTypes — no
  // second backend (CLAUDE.md standing rule 8). Only the presentation is new.
  //
  // Two editors, matching ALPACA's PermitTypes_DetailView and the prototype's
  // "New Permit Type" dialog. The entity's four limit-class flags (MCL / SCL /
  // Alert / MRL) are [VisibleInDetailView(false)] in ALPACA, so they are absent
  // here too; the API treats an unposted flag as "leave unchanged", so omitting
  // them cannot reset stored values.
  'pln-permit-type': {
    basePath: '/api/v1/permit-types',
    idField: 'oid',
    // Audit FormName stays "Permit Types" — the name registered in CLAUDE.md
    // §14b.2 and already present on rows in dbo.AuditData.
    formName: 'Permit Types',
    entityLabel: 'Permit Type',
    inlineRowEdit: false,
    singleColumn: true,
    titleField: 'permitType',
    detailShowDelete: true,
    // New / Edit open the compact "New Permit Type" modal from the approved
    // prototype rather than the full-page detail (owner request 2026-08-27).
    dialogEdit: true,
    fields: [
      { key: 'permitType', label: 'Permit Type', type: 'text', required: true, maxLength: 100,
        // ALPACA PermitTypes.cs:44 — [RuleRequiredField] on PermitType.
        requiredMessage: "'Permit Type' must not be empty." },
      // Payload key stays `definition` (API contract / DB column); only the
      // caption is "Description", per ALPACA's ListView and the prototype.
      // Single-line input (not a textarea) to match the prototype's dialog.
      { key: 'definition', label: 'Description', type: 'text' },
      // Real dbo.PermitTypes.Active column (AI_PermitTypes_Active_Column.sql).
      // Independent of the soft-delete flag: an inactive permit type stays in
      // the list and reads "Inactive"; Delete still hides the row entirely.
      // editOnly — the prototype's "New Permit Type" dialog shows only Permit
      // Type + Description; Active appears on the EDIT dialog. A new row is
      // Active anyway (DF_PermitTypes_Active defaults to 1).
      { key: 'active', label: 'Active', type: 'checkbox', defaultValue: true, editOnly: true },
    ],
  },

  'edi-permit-library': {
    basePath: '/api/v1/permit-libraries',
    idField: 'oid',
    formName: 'Permit Library',
    entityLabel: 'Permit Library',
    fields: [
      { key: 'permitId',       label: 'Permit ID',     type: 'readonly', hint: 'Auto-generated on save' },
      { key: 'permitName',     label: 'Permit Name',   type: 'text',     required: true, maxLength: 100 },
      { key: 'permitTypeId',   label: 'Permit Type',   type: 'select',   required: true, optionsUrl: '/api/v1/permit-types/options', noneLabel: '— Select —' },
      { key: 'permitNumber',   label: 'Permit Number', type: 'text' },
      { key: 'sampleMatrixId', label: 'Sample Matrix', type: 'select',   optionsUrl: '/api/v1/permit-libraries/matrices', noneLabel: '— None —' },
      { key: 'effectiveDate',  label: 'Effective Date',type: 'date' },
      { key: 'endDate',        label: 'End Date',      type: 'date' },
      { key: 'alertSubscribe', label: 'Alert Subscribe', type: 'checkbox' },
      { key: 'emailTo',        label: 'Email To',      type: 'text',     hint: 'Alert recipient(s)' },
      { key: 'remark',         label: 'Remark',        type: 'textarea' },
      { key: 'active',         label: 'Active',        type: 'checkbox' },
      { key: 'retire',         label: 'Retire',        type: 'checkbox', hint: 'Retiring clears Active' },
    ],
  },

  'edi-permit-setup': {
    basePath: '/api/v1/permit-setup',
    idField: 'oid',
    formName: 'Permit Setup',
    entityLabel: 'Permit Limit',
    fields: [
      { key: 'permitLibraryId', label: 'Permit Library', type: 'select', required: true, optionsUrl: '/api/v1/permit-libraries/options', noneLabel: '— Select —' },
      { key: 'parameterId',     label: 'Parameter',      type: 'select', required: true, optionsUrl: '/api/v1/permit-setup/parameters', noneLabel: '— Select —' },
      { key: 'operator',        label: 'Operator',       type: 'select', required: true, optionsUrl: '/api/v1/permit-setup/operators', noneLabel: '— Select —' },
      { key: 'permit',          label: 'Limit Value',    type: 'text',   required: true, maxLength: 100 },
      { key: 'unitsId',         label: 'Units',          type: 'select', optionsUrl: '/api/v1/permit-setup/units', noneLabel: '— None —' },
      { key: 'permit2',         label: '2nd Value',      type: 'text',   hint: 'Only for the Inbetween operator' },
      { key: 'permit2UnitsId',  label: '2nd Units',      type: 'select', optionsUrl: '/api/v1/permit-setup/units', noneLabel: '— None —' },
    ],
  },

  'edi-sheet-mapping': {
    basePath: '/api/v1/edi-sheet-mapping',
    idField: 'oid',
    formName: 'EDI Sheet Mapping',
    entityLabel: 'Sheet Mapping',
    fields: [
      { key: 'sheetName', label: 'Worksheet Name', type: 'text',   required: true, maxLength: 200, hint: 'Exact Excel worksheet name' },
      { key: 'fileType',  label: 'File Type',      type: 'select', required: true, optionsUrl: '/api/v1/edi-sheet-mapping/file-types', noneLabel: '— Select —' },
      { key: 'category',  label: 'Category',       type: 'text',   hint: 'Friendly system/category label' },
      { key: 'isActive',  label: 'Active',         type: 'checkbox' },
    ],
  },
};
